import { DateTime } from 'luxon';

const ISO_8601_FULL = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i;

export const DATETIME_ISO_FORMAT = "yyyy-MM-dd'T'HH:mm";

export const DATETIME_ALARM_FORMAT = 'yyyy.MM.dd. HH:mm';

export const DATETIME_DATE_FORMAT = 'yyyy-MM-dd';

export const DATETIME_DOT_DATE_FORMAT = 'yyyy.MM.dd.';

export const DATETIME_TIME_FORMAT = 'HH:mm';

export const INFINITE_DATE = '9999-12-31T23:59:59.000Z';

export const ReviveDateTime = (key: any, value: any): any => {
  if (typeof value === 'string') {
    const regexResult = ISO_8601_FULL.exec(value);
    if (regexResult) {
      return new Date(value);
    }
  }

  return value;
};

const getZeroFilledStr = (num: number, digit = 2) => String(num).padStart(digit, '0');

// 2010-01-01T01:01:01.001Z
const convertISOLocalDateString = () => {
  const nowDate = new Date();

  const year = nowDate.getFullYear();
  const month = nowDate.getMonth() + 1;
  const date = nowDate.getDate();
  const hour = nowDate.getHours();
  const minute = nowDate.getMinutes();
  const second = nowDate.getSeconds();
  const milliSecond = nowDate.getMilliseconds();

  return `${year}-${getZeroFilledStr(month)}-${getZeroFilledStr(date)}T${getZeroFilledStr(hour)}:${getZeroFilledStr(
    minute,
  )}:${getZeroFilledStr(second)}.${milliSecond}z`;
};

// 2010-01-01T01:01:01.001Z -> 2010-01-01T01:01
export const convertSelectDateToString = () => {
  const dateString = convertISOLocalDateString();
  const [date, time] = dateString.split('T');
  const [hour, min] = time.split(':');
  return `${date}T${hour}:${min}`;
};

// 2010-01-01T01:01 -> 2010-01-01T01:01:00.000Z
export const convertSelectDateToISOLocalString = (selectDate: string) => `${selectDate}:00.000z`;

// 2010-01-01T01:01 -> 2010. 01. 01. 01:01
export const convertToDotString = (convertDate: Date, needHours = true, lineBreakHour = false) => {
  const newDate = new Date(convertDate);

  const year = newDate.getFullYear();
  const month = newDate.getMonth() + 1;
  const date = newDate.getDate();
  const hour = newDate.getHours();
  const minute = newDate.getMinutes();

  const hourString = `${lineBreakHour ? '\n' : ' '}${getZeroFilledStr(hour)}:${getZeroFilledStr(minute)}`;

  return `${year}. ${getZeroFilledStr(month)}. ${getZeroFilledStr(date)}.${needHours ? hourString : ''}`;
};

// 2010-01-01T01:01 -> 2010년 01월 01일 01:01
export const covertToTimeString = (convertDate: Date, needHours = true) => {
  const newDate = new Date(convertDate);

  const year = newDate.getFullYear();
  const month = newDate.getMonth() + 1;
  const date = newDate.getDate();
  const hour = newDate.getHours();
  const minute = newDate.getMinutes();

  const hourString = `${getZeroFilledStr(hour)}:${getZeroFilledStr(minute)}`;

  return `${year}년 ${getZeroFilledStr(month)}월 ${getZeroFilledStr(date)}일 ${needHours ? hourString : ''}`;
};

export const covertToTimeAmPm = (convertDate: Date) =>
  new Date(convertDate).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });

export const convertToRelativeString = (date: Date) => {
  const OneSecondInMilliSeconds = 1000;
  const OneMinuteInMilliSeconds = 60 * OneSecondInMilliSeconds;
  const OneHourInMilliSeconds = OneMinuteInMilliSeconds * 60;
  const OneDayInMilliSeconds = 24 * OneHourInMilliSeconds;
  const OneWeekInMilliSeconds = 7 * OneDayInMilliSeconds;

  const now = Date.now();
  const elapsed = now - new Date(date).getTime();

  if (elapsed < OneMinuteInMilliSeconds) {
    return '방금 전';
  }
  if (elapsed < OneHourInMilliSeconds) {
    return `${Math.floor(elapsed / OneMinuteInMilliSeconds)}분 전`;
  }
  if (elapsed < OneDayInMilliSeconds) {
    return `${Math.floor(elapsed / OneHourInMilliSeconds)}시간 전`;
  }
  if (elapsed < OneWeekInMilliSeconds) {
    return `${Math.floor(elapsed / OneDayInMilliSeconds)}일 전`;
  }
  return convertToDotString(date, false);
};

export const diffDateTimeFormat = (startAt: DateTime, endAt: DateTime) => {
  if (startAt.year !== endAt.year) {
    return endAt.minute ? endAt.toFormat('yyyy년 M월 d일 H시 m분') : endAt.toFormat('yyyy년 M월 d일 H시');
  }
  if (startAt.month !== endAt.month) {
    return endAt.minute ? endAt.toFormat('M월 d일 H시 m분') : endAt.toFormat('M월 d일 H시');
  }
  return endAt.minute ? endAt.toFormat('d일 H시 m분') : endAt.toFormat('d일 H시');
};

export enum WeekDay {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
}

export const WeekDays = [
  WeekDay.Sunday,
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
];

export const LuxonToWeekDay = {
  7: WeekDay.Sunday,
  1: WeekDay.Monday,
  2: WeekDay.Tuesday,
  3: WeekDay.Wednesday,
  4: WeekDay.Thursday,
  5: WeekDay.Friday,
  6: WeekDay.Saturday,
};

export const WeekDayString = {
  [WeekDay.Sunday]: '일',
  [WeekDay.Monday]: '월',
  [WeekDay.Tuesday]: '화',
  [WeekDay.Wednesday]: '수',
  [WeekDay.Thursday]: '목',
  [WeekDay.Friday]: '금',
  [WeekDay.Saturday]: '토',
};

export enum RangeDateStatus {
  Before = 'Before',
  In = 'In',
  After = 'After',
}

export const checkRangeStatus = (startAt: Date, endAt: Date) => {
  const now = DateTime.now().toJSDate();
  if (new Date(startAt) > now) {
    return RangeDateStatus.Before;
  }
  if (new Date(endAt) < now) {
    return RangeDateStatus.After;
  }
  return RangeDateStatus.In;
};

export const isNightMarketing = (sendAt: Date) => {
  const { hour } = DateTime.fromJSDate(new Date(sendAt)).setZone('Asia/Seoul');
  return hour >= 21 || hour < 8;
};
